import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isNil, values as enumValues } from "lodash";
import ReactSelect, { type MultiValue } from "react-select";
import { TariffZoneType } from "../../../../generated/graphql";
import type { BulkBinding, BulkRow, BulkRowStatus } from "./bulk-types";

const styles = {
  nameInput: { minWidth: 160 },
  zoneSelect: { minWidth: 130 },
  contactSelect: { minWidth: 220 },
  message: { whiteSpace: "normal", wordBreak: "break-word", maxWidth: 280 },
} satisfies Record<string, SxProps<Theme>>;

type ContactOption = { value: string; label: string };

type StatusDisplay = {
  label: string;
  color: "default" | "info" | "success" | "error" | "warning";
};

/**
 * Maps a row status to a chip label and color for the grid.
 *
 * @param status The row's lifecycle status.
 * @returns The label and MUI chip color to render.
 */
const statusDisplay = (status: BulkRowStatus): StatusDisplay => {
  switch (status) {
    case "pending": {
      return { label: "Pending", color: "default" };
    }
    case "uploading": {
      return { label: "Uploading", color: "info" };
    }
    case "ready": {
      return { label: "Ready", color: "default" };
    }
    case "running": {
      return { label: "Running", color: "info" };
    }
    case "dry-run-ok": {
      return { label: "Preview OK", color: "success" };
    }
    case "dry-run-unavailable": {
      return { label: "No preview", color: "warning" };
    }
    case "imported": {
      return { label: "Imported", color: "success" };
    }
    case "error": {
      return { label: "Error", color: "error" };
    }
    default: {
      return { label: status, color: "default" };
    }
  }
};

type BulkGridProps = {
  readonly rows: BulkRow[];
  readonly contactOptions: ContactOption[];
  readonly busy: boolean;
  readonly onUpdateBinding: (id: string, patch: Partial<BulkBinding>) => void;
  readonly onRemoveRow: (id: string) => void;
};

/**
 * Renders the per-tariff bulk grid: one row per uploaded CSV with inline editing
 * of the high-variance bindings (name, contacts, zone type), a live status chip,
 * and the dry-run/import result or error. Imported rows are locked (their inputs
 * disabled) so the non-idempotent engine cannot create duplicate tariff groups.
 *
 * @param props.rows The bulk rows to display.
 * @param props.contactOptions Selectable customer options.
 * @param props.busy Whether a batch run is in progress (disables editing).
 * @param props.onUpdateBinding Inline-edit callback for a row's binding.
 * @param props.onRemoveRow Remove-row callback.
 */
export const BulkGrid = ({
  rows,
  contactOptions,
  busy,
  onUpdateBinding,
  onRemoveRow,
}: BulkGridProps) => {
  if (rows.length === 0) {
    return (
      <Typography variant="body2">
        No CSVs loaded yet. Upload tariff CSVs above to begin.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>CSV file</TableCell>
          <TableCell>Tariff name</TableCell>
          <TableCell>Customers</TableCell>
          <TableCell>Zone type</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Result / error</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const locked = row.status === "imported" || busy;
          const display = statusDisplay(row.status);
          const selectedContacts = new Set(row.binding.contactUuids);
          return (
            <TableRow key={row.id}>
              <TableCell>{row.fileName}</TableCell>
              <TableCell>
                <TextField
                  size="small"
                  sx={styles.nameInput}
                  disabled={locked}
                  value={row.binding.tariffName}
                  onChange={(event) => {
                    onUpdateBinding(row.id, { tariffName: event.target.value });
                  }}
                />
              </TableCell>
              <TableCell>
                <Box sx={styles.contactSelect}>
                  <ReactSelect
                    isMulti
                    menuPosition="fixed"
                    isDisabled={locked}
                    options={contactOptions}
                    value={contactOptions.filter((option) =>
                      selectedContacts.has(option.value),
                    )}
                    placeholder="Customers"
                    onChange={(option: MultiValue<ContactOption>) => {
                      onUpdateBinding(row.id, {
                        contactUuids: option.map((item) => item.value),
                      });
                    }}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Select
                  size="small"
                  sx={styles.zoneSelect}
                  disabled={locked}
                  value={row.binding.tariffZoneType}
                  onChange={(event) => {
                    onUpdateBinding(row.id, {
                      tariffZoneType: event.target.value as TariffZoneType,
                    });
                  }}
                >
                  {enumValues(TariffZoneType).map((zoneType) => (
                    <MenuItem key={zoneType} value={zoneType}>
                      {zoneType}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={display.color}
                  label={display.label}
                />
              </TableCell>
              <TableCell>
                <Typography
                  variant="caption"
                  color={row.status === "error" ? "error" : "textSecondary"}
                  sx={styles.message}
                >
                  {row.message ??
                    (isNil(row.tariffsToCreate)
                      ? ""
                      : `${row.tariffsToCreate} tariffs / ${row.rangesToCreate} ranges`)}
                </Typography>
                {!isNil(row.manifestWarning) && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    display="block"
                    sx={styles.message}
                  >
                    {row.manifestWarning}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Tooltip title="Remove row">
                  <span>
                    <IconButton
                      size="small"
                      disabled={locked}
                      onClick={() => {
                        onRemoveRow(row.id);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
